import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail, STAGES, STAGE_LABELS, DOC_TYPES, API, fileToBase64, triggerNativeDownload } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Lock, Circle, FileText, Upload, Camera, Ship, Anchor, AlertTriangle,
  Download, MessageCircle, Paperclip, ShieldCheck, Trash2, Loader2, Eye, Paperclip as Clip,
  History, UploadCloud, X, ArrowRight,
} from "lucide-react";
import SimpleUploadStep from "@/components/steps/SimpleUploadStep";
import StepVoyage from "@/components/steps/StepVoyage";
import StepDelivery from "@/components/steps/StepDelivery";
import StepClosure from "@/components/steps/StepClosure";
import CommentsThread from "@/components/CommentsThread";

export default function ShipmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isBuyer = user?.role === "buyer";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);

  const reload = useCallback(async () => {
    const { data } = await api.get(`/shipments/${id}`);
    setData((prev) => {
      // If the server advanced the stage, auto-focus the new current stage
      if (prev && prev.shipment.current_stage !== data.shipment.current_stage) {
        setActiveStage(data.shipment.current_stage);
      } else if (!prev) {
        setActiveStage(data.shipment.current_stage);
      }
      return data;
    });
    return data;
  }, [id]);

  useEffect(() => {
    api.get(`/shipments/${id}`)
      .then((r) => {
        setData(r.data);
        setActiveStage(r.data.shipment.current_stage);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-6 text-stone-400">Loading…</div>;
  if (!data) return <div className="p-6 text-stone-400">Not found</div>;

  const { shipment, buyer } = data;
  const currentIdx = STAGES.indexOf(shipment.current_stage);
  const completed = shipment.completed_stages || [];

  const stageStatus = (stage) => {
    if (completed.includes(stage)) return "completed";
    // Closure step is "completed" once shipment.status === completed
    if (stage === "closure" && shipment.status === "completed") return "completed";
    if (stage === shipment.current_stage) return "active";
    const si = STAGES.indexOf(stage);
    return si < currentIdx ? "completed" : "locked";
  };

  const handleStageClick = (stage) => {
    const status = stageStatus(stage);
    if (status === "locked") {
      toast.error("Complete previous step first");
      return;
    }
    setActiveStage(stage);
  };

  return (
    <div className="p-5 space-y-5">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#6B0F1A]" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {isBuyer && (
        <div className="bg-[#F4E8E9] border border-[#6B0F1A]/15 text-[#6B0F1A] rounded-xl px-4 py-2.5 text-xs font-semibold flex items-center gap-2" data-testid="buyer-readonly-banner">
          <Eye className="w-3.5 h-3.5" /> Read-only buyer view. You can collaborate via comments anytime.
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Shipment</div>
            <h1 className="font-display text-base sm:text-lg font-bold tracking-tight text-[#1F1F1F] mt-0.5 truncate font-mono" data-testid="shipment-reference">
              {shipment.reference}
            </h1>
            <div className="text-sm font-semibold text-[#1F1F1F] mt-1.5 truncate">
              {buyer?.company_name || "—"}
            </div>
            <div className="text-xs text-stone-500 truncate">
              {shipment.product}{shipment.variety ? ` · ${shipment.variety}` : ""}
            </div>
            {shipment.sla_reference && (
              <div className="text-[10px] font-mono text-stone-400 mt-1.5" data-testid="shipment-sla-reference">
                SLA: {shipment.sla_reference}
              </div>
            )}
          </div>
          <StatusBadgeBig status={shipment.status} />
        </div>
        {!isBuyer && (
          <div className="mt-4 pt-3 border-t border-stone-100">
            <Link
              to={`/app/shipments/${shipment.id}/documents`}
              data-testid="open-documents-btn"
              className="inline-flex items-center gap-2 bg-[#F4E8E9] hover:bg-[#EBD3D6] text-[#6B0F1A] border border-[#6B0F1A]/15 text-sm font-semibold rounded-xl px-4 py-2.5"
            >
              <FileText className="w-4 h-4" />
              Generate / Edit Documents
            </Link>
          </div>
        )}
      </div>

      {/* Pipeline */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 sm:p-5" data-testid="pipeline">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Pipeline</div>
          <div className="flex items-center gap-2">
            {(data.audit_logs || []).length > 0 && (
              <button
                onClick={() => setActivityOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#6B0F1A] hover:bg-[#F4E8E9] border border-[#6B0F1A]/20 rounded-full px-2.5 py-1"
                data-testid="open-activity-log"
              >
                <History className="w-3 h-3" /> Activity ({(data.audit_logs || []).length})
              </button>
            )}
            <div className="text-[10px] text-stone-400 inline-flex items-center gap-1">
              <Camera className="w-3 h-3" /> + <Upload className="w-3 h-3" /> per step
            </div>
          </div>
        </div>
        <ol className="relative space-y-4">
          {STAGES.map((stage, idx) => {
            const status = stageStatus(stage);
            const isLast = idx === STAGES.length - 1;
            const stageDocs = (data.documents || []).filter((d) => d.doc_type === "stage_inline" && d.stage === stage);
            return (
              <li key={stage} className="relative pl-12">
                {!isLast && (
                  <span
                    className={`absolute left-4 top-8 bottom-[-1rem] w-[2px] ${
                      completed.includes(stage) ? "bg-[#A8323F]" : "bg-stone-200"
                    }`}
                  />
                )}
                <div className={`flex items-center gap-2 ${status === "locked" ? "opacity-60" : ""}`}>
                  <button
                    onClick={() => handleStageClick(stage)}
                    data-testid={`stage-${stage}`}
                    className="flex-1 text-left flex items-center gap-3 group min-w-0"
                  >
                    <span
                      className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                        status === "completed"
                          ? "bg-[#A8323F] text-white"
                          : status === "active"
                          ? "bg-[#6B0F1A] text-white ring-4 ring-[#F4E8E9] pulse-dot"
                          : "bg-stone-100 border-2 border-stone-300 text-stone-400"
                      }`}
                    >
                      {status === "completed" ? <CheckCircle2 className="w-4 h-4" /> : status === "locked" ? <Lock className="w-3.5 h-3.5" /> : idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm flex items-center gap-1.5 flex-wrap ${activeStage === stage ? "text-[#6B0F1A]" : status === "locked" ? "text-stone-400" : "text-[#1F1F1F]"}`}>
                        <span>{STAGE_LABELS[stage]}</span>
                        {stageDocs.length > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-[#F4E8E9] text-[#6B0F1A] rounded-full px-1.5 py-0.5" data-testid={`stage-doc-count-${stage}`}>
                            <Clip className="w-2.5 h-2.5" />{stageDocs.length}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-stone-500">
                        {shipment.pending_approval_stage === stage
                          ? "Awaiting owner approval"
                          : stage === "closure" && shipment.status === "completed"
                          ? "No claim · Closed"
                          : stage === "closure" && shipment.status === "claim"
                          ? "Claim open"
                          : status === "completed"
                          ? "Completed"
                          : status === "active"
                          ? "In progress"
                          : "Locked"}
                      </div>
                    </div>
                  </button>
                  <StageInlineUpload
                    shipmentId={shipment.id}
                    stage={stage}
                    disabled={status === "locked"}
                    onUploaded={reload}
                  />
                </div>
                {stageDocs.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`stage-docs-${stage}`}>
                    {stageDocs.map((d) => (
                      <StageDocChip key={d.id} doc={d} shipmentId={shipment.id} canDelete={!!user?.is_owner} onChanged={reload} />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Active step card */}
      <div
        className="bg-white border border-stone-200 rounded-2xl p-5 fade-up"
        data-testid={`step-card-${activeStage}`}
        data-buyer-ro={isBuyer ? "true" : undefined}
      >
        <StepContent stage={activeStage} data={data} reload={reload} />
      </div>

      {/* Comments */}
      <CommentsThread
        shipmentId={shipment.id}
        comments={data.comments}
        onRefresh={reload}
        canCollaborate={true}
        currentUserId={user?.id}
        isBuyer={isBuyer}
      />

      {/* Meta footer - created / last modified */}
      {(shipment.created_at || shipment.last_modified_by_name) && (
        <div className="text-[11px] text-stone-400 grid grid-cols-1 sm:grid-cols-2 gap-2 px-1 pb-2" data-testid="shipment-meta">
          {shipment.created_at && (
            <div>
              <span className="font-semibold uppercase tracking-wider text-[10px]">Created</span>
              <div className="text-stone-500">
                {new Date(shipment.created_at).toLocaleString()}
                {shipment.created_by_name && <> · by <span className="text-stone-700">{shipment.created_by_name}</span></>}
              </div>
            </div>
          )}
          {(shipment.updated_at || shipment.last_modified_by_name) && (
            <div className="sm:text-right">
              <span className="font-semibold uppercase tracking-wider text-[10px]">Last modified</span>
              <div className="text-stone-500">
                {shipment.updated_at ? new Date(shipment.updated_at).toLocaleString() : "—"}
                {shipment.last_modified_by_name && <> · by <span className="text-stone-700">{shipment.last_modified_by_name}</span></>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Activity log modal */}
      {activityOpen && createPortal(
        <ActivityLogModal audit={data.audit_logs || []} onClose={() => setActivityOpen(false)} />,
        document.body
      )}
    </div>
  );
}

function ActivityLogModal({ audit, onClose }) {
  const [filter, setFilter] = useState("all"); // all | uploaded | deleted
  const filtered = useMemo(() => {
    if (filter === "all") return audit;
    return audit.filter((e) => e.action === filter);
  }, [audit, filter]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="activity-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] sm:max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3 border-b border-stone-100">
          <div>
            <h3 className="font-display text-lg font-extrabold text-[#1F1F1F] flex items-center gap-2">
              <History className="w-4 h-4 text-[#6B0F1A]" /> Activity Log
            </h3>
            <p className="text-[11px] text-stone-500">
              All document uploads and deletions for this shipment.
            </p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 -mt-1" aria-label="Close" data-testid="close-activity-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filter pills */}
        <div className="px-5 py-3 flex gap-1.5 border-b border-stone-100">
          {[
            { key: "all", label: "All", count: audit.length },
            { key: "uploaded", label: "Uploads", count: audit.filter((e) => e.action === "uploaded").length },
            { key: "deleted", label: "Deletions", count: audit.filter((e) => e.action === "deleted").length },
          ].map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${
                  active
                    ? "bg-[#6B0F1A] text-white border-[#6B0F1A]"
                    : "bg-white text-stone-600 border-stone-200 hover:border-[#A8323F]"
                }`}
                data-testid={`activity-filter-${f.key}`}
              >
                {f.label} <span className={`ml-1 ${active ? "text-white/80" : "text-stone-400"}`}>{f.count}</span>
              </button>
            );
          })}
        </div>

        {/* Events list */}
        <div className="overflow-y-auto px-5 py-3 flex-1" data-testid="activity-events">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-stone-400">
              <History className="w-6 h-6 mx-auto opacity-40" />
              <div className="text-sm mt-2">No events yet</div>
            </div>
          ) : (
            <ul className="space-y-2.5">
              {filtered.map((e) => {
                const isDelete = e.action === "deleted";
                return (
                  <li
                    key={e.id}
                    className={`rounded-xl border p-3 ${isDelete ? "bg-red-50/50 border-red-100" : "bg-emerald-50/50 border-emerald-100"}`}
                    data-testid={`activity-event-${e.id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isDelete ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}`}>
                        {isDelete ? <Trash2 className="w-3.5 h-3.5" /> : <UploadCloud className="w-3.5 h-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className={`text-xs font-bold ${isDelete ? "text-red-700" : "text-emerald-700"}`}>
                            {isDelete ? "Deleted" : "Uploaded"}
                          </span>
                          {e.stage && (
                            <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">
                              · {STAGE_LABELS[e.stage] || e.stage}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[#1F1F1F] font-mono break-all mt-0.5">{e.filename || "—"}</div>
                        <div className="text-[11px] text-stone-500 mt-1">
                          by <span className="text-stone-700 font-semibold">{e.by_name || "—"}</span>
                          <span className="text-stone-400"> · {new Date(e.at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function StepContent({ stage, data, reload }) {
  switch (stage) {
    case "buyer":
      return <SimpleUploadStep
        data={data} reload={reload}
        stage="buyer"
        stepNumber={1}
        title="Buyer Registration"
        description="Upload company KYC documents (Trade License, KYC, bank reference). Mark third-party credit analysis as completed, then submit for owner approval."
        nextStageLabel="Service Agreement"
        gated requireCreditAnalysis
      />;
    case "sla":
      return <SimpleUploadStep
        data={data} reload={reload}
        stage="sla"
        stepNumber={2}
        title="Service Level Agreement"
        description="Upload the signed SLA and any addendums. Submit for owner approval to proceed."
        nextStageLabel="Sales Confirmation"
        gated
      />;
    case "sales_confirmation":
      return <SimpleUploadStep
        data={data} reload={reload}
        stage="sales_confirmation"
        stepNumber={3}
        title="Sales Confirmation"
        description="Upload the signed Sales Confirmation. Submit for owner approval to proceed."
        nextStageLabel="Documentation"
        gated
      />;
    case "documentation":
      return <SimpleUploadStep
        data={data} reload={reload}
        stage="documentation"
        stepNumber={4}
        title="Pre-Shipment Documentation"
        description="Upload Packing List, Phytosanitary, Certificate of Origin, PSQIR, and the Commercial Invoice."
        nextStageLabel="Vessel & Voyage"
      />;
    case "voyage":
      return <StepVoyage data={data} reload={reload} />;
    case "delivery":
      return <StepDelivery data={data} reload={reload} />;
    case "closure":
      return <StepClosure data={data} reload={reload} />;
    default:
      return null;
  }
}

function StepBuyerView({ buyer }) {
  if (!buyer) return <div className="text-sm text-stone-500">Buyer not found</div>;
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Step 1</div>
      <h2 className="font-display text-xl font-bold tracking-tight text-[#1F1F1F] mt-1">Buyer Registration</h2>
      <p className="text-sm text-stone-500 mb-4">Completed. Buyer linked to this shipment.</p>
      <div className="space-y-2 text-sm">
        <Row k="Company" v={buyer.company_name} />
        <Row k="Contact" v={buyer.contact_person} />
        <Row k="Email" v={buyer.email} />
        <Row k="Phone" v={buyer.phone} />
        <Row k="Country" v={buyer.country} />
        <Row k="Address" v={buyer.address} />
      </div>
    </div>
  );
}

export function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-stone-100 py-1.5">
      <span className="text-stone-500 text-xs uppercase font-bold tracking-wider">{k}</span>
      <span className="text-[#1F1F1F] font-medium text-sm text-right max-w-[60%] break-words">{v || "—"}</span>
    </div>
  );
}

function StatusBadgeBig({ status }) {
  const map = {
    active: { c: "bg-[#F4E8E9] text-[#6B0F1A]", l: "Active" },
    completed: { c: "bg-stone-100 text-stone-600", l: "Completed" },
    claim: { c: "bg-amber-100 text-amber-800", l: "Claim" },
  };
  const s = map[status] || { c: "bg-stone-100", l: status };
  return (
    <span className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap ${s.c}`}>
      {s.l}
    </span>
  );
}


function StageInlineUpload({ shipmentId, stage, disabled, onUploaded }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/shipments/${shipmentId}/stage-uploads`, {
        stage,
        filename: file.name || `upload-${Date.now()}`,
        content_base64: b64,
        mime_type: file.type || "application/octet-stream",
      });
      toast.success("Uploaded");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1 shrink-0">
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => cameraInputRef.current?.click()}
        className="w-8 h-8 rounded-lg border border-stone-200 hover:border-[#A8323F] hover:bg-[#F4E8E9] text-stone-600 hover:text-[#6B0F1A] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        data-testid={`stage-camera-${stage}`}
        aria-label="Capture photo"
        title="Capture photo"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => fileInputRef.current?.click()}
        className="w-8 h-8 rounded-lg border border-stone-200 hover:border-[#A8323F] hover:bg-[#F4E8E9] text-stone-600 hover:text-[#6B0F1A] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        data-testid={`stage-upload-${stage}`}
        aria-label="Upload file"
        title="Upload file"
      >
        <Upload className="w-3.5 h-3.5" />
      </button>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
}

function StageDocChip({ doc, shipmentId, canDelete, onChanged }) {
  const [busy, setBusy] = useState(false);
  // When a doc is a buyer-level master anchored to a different shipment, it is
  // read-only from the current shipment. Modifications must be made on the
  // original shipment, which re-triggers the owner-approval flow there.
  const isForeignMaster = !!(doc.is_master && doc.source_shipment_id && doc.source_shipment_id !== shipmentId);
  const view = async () => {
    setBusy(true);
    try {
      if ((doc.mime_type || "").startsWith("image/")) {
        const r = await api.get(`/shipments/${shipmentId}/documents/${doc.id}`);
        const b64 = r.data?.content_base64;
        if (!b64) { toast.error("Could not fetch document"); return; }
        const w = window.open();
        if (!w) { toast.error("Popup blocked — allow popups to preview images"); return; }
        w.document.write(`<img src="${b64}" style="max-width:100%" alt="${doc.filename}" />`);
        return;
      }
      const r = await api.post(`/shipments/${shipmentId}/documents/${doc.id}/download-token`);
      triggerNativeDownload(r.data.download_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not fetch document");
    } finally { setBusy(false); }
  };
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${doc.filename}?\n\nIf this document was approved under steps 1, 2 or 3, the shipment will roll back to that step and need re-approval.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/shipments/${shipmentId}/stage-uploads/${doc.id}`);
      if (r.data?.reverted) {
        toast.warning("Step rolled back — re-upload and resubmit for approval");
      } else {
        toast.success("Document removed");
      }
      onChanged && onChanged();
    } catch (er) {
      toast.error(formatApiErrorDetail(er?.response?.data?.detail) || er.message);
    } finally { setBusy(false); }
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 border rounded-full pl-2 pr-1 py-1 text-[11px] max-w-full ${
        isForeignMaster
          ? "bg-emerald-50 border-emerald-200 text-emerald-900"
          : "bg-stone-50 hover:bg-stone-100 border-stone-200 text-stone-700"
      }`}
      data-testid={`stage-doc-chip-${doc.id}`}
      title={isForeignMaster ? `Master document from ${doc.source_shipment_reference}` : doc.filename}
    >
      <Paperclip className={`w-3 h-3 shrink-0 ${isForeignMaster ? "text-emerald-700" : "text-[#6B0F1A]"}`} />
      <button onClick={view} className="truncate max-w-[160px] hover:underline" title={doc.filename}>
        {doc.filename}
      </button>
      {isForeignMaster && (
        <>
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded-full"
            data-testid={`stage-doc-master-${doc.id}`}
          >
            Master
          </span>
          <Link
            to={`/app/shipments/${doc.source_shipment_id}`}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded-full hover:bg-emerald-200 text-emerald-700 flex items-center justify-center"
            title={`Open original shipment (${doc.source_shipment_reference || ""}) to edit`}
            data-testid={`stage-doc-open-original-${doc.id}`}
          >
            <ArrowRight className="w-2.5 h-2.5" />
          </Link>
        </>
      )}
      {canDelete && !isForeignMaster && (
        <button onClick={remove} disabled={busy} className="w-4 h-4 rounded-full hover:bg-red-100 text-stone-400 hover:text-red-600 flex items-center justify-center" data-testid={`stage-doc-delete-${doc.id}`}>
          <Trash2 className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}
